import { useEffect, useState } from 'react';
import type { ReviewModeration } from '@salonomy/types';
import { Button, Card, Field, SegmentedControl, Select, Spinner, Toggle } from '@salonomy/ui';
import { useReviewSettings, useUpdateReviewSettings } from '../../data/reviews.js';
import { useToast } from '../../lib/toast.js';
import './reviews.css';

const MODERATION_OPTIONS: Array<{ value: ReviewModeration; label: string }> = [
  { value: 'auto', label: 'Auto-publish' },
  { value: 'manual', label: 'Review first' },
];

// Auto-request delay presets (minutes). The API accepts 0..43200 (30 days); these cover the useful span.
const DELAY_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Immediately' },
  { value: 60, label: '1 hour later' },
  { value: 180, label: '3 hours later' },
  { value: 1440, label: '1 day later' },
  { value: 2880, label: '2 days later' },
  { value: 10080, label: '1 week later' },
];

/**
 * Per-salon review settings (manager-only). Two controls:
 *  - moderation mode: 'auto' (new reviews go live immediately — best for SEO, the default) vs 'manual'
 *    (new reviews wait in Pending until a manager publishes them).
 *  - auto-request: when on, the server mints a review request automatically a chosen delay after an
 *    appointment is marked completed. Off → requests are only ever sent by hand from the diary.
 *
 * Local form state seeds from the server settings and is reconciled whenever they (re)load; a single
 * Save sends a partial UpsertReviewSettings. The server is authoritative — this panel never derives or
 * persists anything itself.
 */
export function ReviewSettingsPanel() {
  const { data, isLoading, isError } = useReviewSettings();
  const update = useUpdateReviewSettings();
  const toast = useToast();

  const [moderation, setModeration] = useState<ReviewModeration>('auto');
  const [autoRequest, setAutoRequest] = useState(false);
  const [delay, setDelay] = useState(1440);
  const [busy, setBusy] = useState(false);

  // Seed/reconcile the form from the server whenever the settings load or change.
  useEffect(() => {
    if (!data) return;
    setModeration(data.moderation);
    setAutoRequest(data.autoRequestEnabled);
    setDelay(data.autoRequestDelayMinutes);
  }, [data]);

  async function save() {
    setBusy(true);
    try {
      await update.mutateAsync({
        moderation,
        autoRequestEnabled: autoRequest,
        autoRequestDelayMinutes: delay,
      });
      toast.success('Review settings saved');
    } catch {
      toast.error('Could not save your review settings.');
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading review settings…
          </span>
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load your review settings. Please try again.
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="section-title" style={{ marginBottom: 2 }}>
        Review settings
      </div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        How new reviews are handled and whether requests go out automatically.
      </div>

      <div className="reviews-settings-grid">
        <Field
          label="New reviews"
          hint={
            moderation === 'auto'
              ? 'New reviews go live straight away. You can still hide any review afterwards.'
              : 'New reviews wait in Pending until you publish them. Best if you want a final say.'
          }
        >
          <SegmentedControl
            options={MODERATION_OPTIONS}
            value={moderation}
            onChange={setModeration}
          />
        </Field>

        <div>
          <Toggle
            checked={autoRequest}
            onChange={setAutoRequest}
            label="Automatically ask for a review after each visit"
          />
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5 }}>
            When on, a review request is sent automatically once an appointment is marked completed.
            When off, you can still request a review by hand from the diary.
          </div>
        </div>

        {autoRequest ? (
          <Field label="Send the request" hint="Measured from when the appointment is completed.">
            <Select value={String(delay)} onChange={(e) => setDelay(Number(e.target.value))}>
              {DELAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <div>
          <Button onClick={save} disabled={busy}>
            Save settings
          </Button>
        </div>
      </div>
    </Card>
  );
}
